import React, {useState, useEffect} from 'react';
import { Modal as ModalComponent, closeModal } from '../common/Modal.jsx';
import { Portal } from '../common/Portal.jsx';
import LoadingIndicator from '../common/loading_indicator.jsx';
import RichText from '../common/RichText.jsx';
import { fetchJson } from '../../helpers/index.js';
import update from 'immutability-helper';

import {
  user_notification_get_path,
  user_notification_mark_as_read_path,
  user_notification_incoming_notifications_path,
  user_notification_clear_all_path,
  user_notification_delete_recipient_path
} from '~/routes';

const MODAL_ID = 'user-notification-popup';

const COLOR_MAPPING = {
  other: {
    background: '#fcefe3',
    icon: '#fd9644'
  },
  live_discussion: {
    background: '#eefaff',
    icon: '#45aaf2'
  }
}

export const UserNotificationModal = ({handleCloseModal, handleMarkAsRead, selectedItem}) => {
  const [loading, setLoading] = useState();
  const [saving, setSaving] = useState(false);
  const [notification, setNotification] = useState({});

  useEffect(() => {
    setLoading(true);

    fetchJson(user_notification_get_path({id: selectedItem.id})).then(res => {
      setNotification(res);
      setLoading(false);
    });
  }, []);

  const handleClose = () => {
    closeModal(MODAL_ID);
    handleCloseModal();
  };

  const handleSave = (e) => {
    e.preventDefault();
    setSaving(true);

    jQuery.post(user_notification_mark_as_read_path({id: selectedItem.id, _options: true}), {})
      .done((_res) => {
        handleMarkAsRead(selectedItem.idx);
        setSaving(false);
        handleClose();
      });
  };

  return (
    <Portal
      id={MODAL_ID}
      size='lg'
      title={selectedItem.title}
      onClose={handleClose}
      cancelLabel={I18n.t('close')}
      cssClassNameCancelButton={'btn-primary pull-right'}
      onSave={handleSave}
      saving={saving}
      saveLabel={I18n.t('ok')}
      hideSaveButton={selectedItem.read}
      hideCloseButton={!selectedItem.read}
      modalClassName={'modal-dialog-centered'}
      component={ModalComponent}
    >
      { loading ?
        <LoadingIndicator/> : <RichText fieldname='message' value={notification?.message} preview={true} readOnly={true} />
      }
    </Portal>
  );
};

const UserNotificationDropdownMenu = ({}) => {
  const [loaded, setLoaded] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState({});
  const [notifications, setNotifications] = useState([]);

  // handle onClose event of the modal
  const handleCloseModal = () => setOpenModal(false);

  // handle onClick event of the dropdown
  const handleClick = (e, idx, notification) => {
    if (notification.notification_type === 'live_discussion'){
        jQuery.post(user_notification_mark_as_read_path({id: notification.id, _options: true}), {})
        .done((_res) => {
          handleMarkAsRead(idx);
        });
    } else {
      setSelectedItem({...notification, idx})
      setOpenModal(true);
    }
  };

  const handleMarkAsRead = (idx) => {
    setNotifications(update(notifications, { [idx]: { $merge: { read: true } } }));
  };

  const handleClearALLClick = (e) => {
    e.stopPropagation();

    if (confirm(I18n.t('clear_all_confirm'))) {
      jQuery
        .post(user_notification_clear_all_path(), () => {
          setNotifications([]);
        })
        .fail(() => {
          alert(I18n.t('error_delete_notification'));
        });
    }
  };

  const handleDeleteUserNotification = (id) => {
    if (confirm(I18n.t('confirm_delete_notification'))){
      jQuery
        .post(user_notification_delete_recipient_path({_options: true, id: id}), () => {
          setNotifications(notis => notis.filter((n) => n.id !== id))
        })
        .fail(() => {
          alert(I18n.t('error_delete_notification'))
        })
    }
  }

  useEffect(() => {
    fetchJson(user_notification_incoming_notifications_path({})).then(res => {
      setNotifications(res);
      setLoaded(true);
    });
  }, []);

  const unreadNotificationCount = loaded && notifications.filter((r) => !r.read).length;

  return (<>

        <a href='#' className='dropdown-toggle' data-toggle='dropdown'>
          <div className='notification-icon'>
            <i className='fa fa-bell-o'></i> 
            <span 
              className={`label ${unreadNotificationCount > 0 ? 'label-warning' : 'label-default'}`} 
              style={{minWidth: '20px'}}
            >
              {loaded ? unreadNotificationCount : <span>&nbsp;</span>}
            </span>
          </div>
        </a>
        <ul className='dropdown-menu p-0 m-0 border-0 notification-menu' onClick={(e) => {e.stopPropagation();}}>
          <div className='p-1 text-bold text-uppercase notification-header'>
            <span>{I18n.t('notifications')}</span>
            <span onClick={handleClearALLClick} className='btn-link clear-all'>{I18n.t('clear_all')}</span>
          </div>
          <div className='notification-list'>
            {loaded && notifications.length == 0 &&
              <div className='no-notifications'>
                <span className='text-muted' >{I18n.t('no_user_notifications')}</span>
              </div>}
            {loaded && notifications.map((v, i) => (
                <li key={i} className={`list-group-item notification-item`} style={{backgroundColor: v.read ? 'white' :( v.metadata ? COLOR_MAPPING.live_discussion.background : COLOR_MAPPING.other.background)}}>
                  {
                    (v.metadata && Object.keys((v.metadata || {}).discussions || {}).length > 1) ? 
                    <LiveDiscussionBulkNotification notification={v} notiIndex={i} handleClick={handleClick} />
                    :
                    <SingleNotification notification={v} notiIndex={i} handleClick={handleClick} /> 
                  }
                  <button onClick={(e) => handleDeleteUserNotification(v.id)} className='close'>
                    <span className="glyphicon glyphicon-remove" style={{fontSize: '15px'}}></span>
                  </button>
                </li>
              ))
            }
          </div>
        </ul>
    {openModal && <UserNotificationModal {...{handleCloseModal, handleMarkAsRead, selectedItem}}></UserNotificationModal>}
  </>);
};

const LiveDiscussionBulkNotification = ({notification, handleDirectRedirect, notiIndex, handleClick}) => {
  const [open, setOpen] = useState(false)
  const filteredDiscussions = {} // Check for unique identifiers
  const metadata = notification.metadata || {}
  for (const [key, value] of Object.entries(notification.metadata.discussions)) {
    if (!(value.identifier in filteredDiscussions)) {
      filteredDiscussions[value.identifier] = key;
    }
  }
  return (
    Object.keys(filteredDiscussions).length === 1 ? 
    <SingleNotification  {...{notiIndex, handleClick, notification}} />
    :
    <div style={{backgroundColor: notification.read ? 'white' : COLOR_MAPPING.live_discussion.background}}>
      <div className='no-style bulk-live-discussion'>
        <div className='d-flex'>
          <i className="fa-solid fa-comment mr-1" style={{color: COLOR_MAPPING.live_discussion.icon}}></i>
          <a href ='#' onClick={() => setOpen(prev => !prev)} className={`discussion-message-notification ${notification.read ? '' : 'fw-bold'} color-text-notification text-decoration-none`}>
            {I18n.t('bulk_submission_message_notification', {discussion_num: Object.keys(filteredDiscussions).length})}
          </a>
        </div>
        {open && 
        <ul style={{listStyleType: 'none'}}>
          { Object.values(filteredDiscussions)
            .map((k) => 
                  <li key={`discussion-${k}`}>
                    <a href={((metadata.discussions || {})[metadata.current_discussion] || {}).redirect} onClick={(e) => handleClick(e, notiIndex , notification)} className='text-decoration-none'>
                      {notification.metadata.discussions[k].identifier}
                    </a>
                  </li>
          )}
        </ul>}
      </div>
      <small className='text-muted'>{DT.dt(notification.published_at, {datetime: true})}</small>
    </div>
  )
}

const SingleNotification = ({notification, notiIndex, handleClick}) => {
  const metadata = notification.metadata || {}
  return (
    <a href={((metadata.discussions || {})[metadata.current_discussion] || {}).redirect}
      className={'color-text-notification text-decoration-none'} 
      onClick={(e) => handleClick(e, notiIndex , notification)} 
      style={{backgroundColor: notification.read ? 'white' : (notification.metadata ? COLOR_MAPPING.live_discussion.background : COLOR_MAPPING.other.background), cursor: 'pointer', width: '100%'}}>
      <div className='d-flex'>
        <i className={`fa-solid ${notification.metadata ? 'fa-comment' : 'fa-bullhorn'} mr-1`} style={{color: notification.metadata ? COLOR_MAPPING.live_discussion.icon : COLOR_MAPPING.other.icon}}></i>
        <div className={`${notification.read ? '' : 'fw-bold'} line-clamp`}>{notification.title}</div>
      </div>
      <small className='text-muted'>{DT.dt(notification.published_at, {datetime: true})}</small>
    </a>
  )

}

export default UserNotificationDropdownMenu;
